import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getEntry,
  getEntryChipHistory,
  getEntryEventHistory,
  getEntrySeasonHistory,
} from "core-integration/src/store/entries/reducers";
import {
  fetchEntryHistory,
  fetchEntrySummary,
} from "core-integration/src/store/entries/thunks";
import { getEventsById } from "core-integration/src/store/events/reducers";
import { formatRawAsLocalI18n } from "core-integration/src/utils/datetime";
import { integerToMoney } from "core-integration/src/utils/money";
import React, { useEffect } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Box } from "rebass/styled-components";
import { dateLocales } from "../i18n";
import { ChipName, getChipName } from "../utils/chips";
import { integerToDecimal } from "../utils/decimal";
import { getPrefixedEventShortName } from "../utils/events";
import { integerDivide } from "../utils/points";
import Entry from "./Entry";
import { Main, Secondary, Wrapper } from "./Layout";
import Movement from "./leagues/Movement";
import Link from "./Link";
import SubHeading from "./SubHeading";
import Table, { ScrollTable } from "./Table";
import Title from "./Title";
import Tooltip, { TooltipLabel } from "./Tooltip";
import Panel from "./Panel";

type Props = WithTranslation;

const EntryHistory: React.FC<Props> = ({ i18n, t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { entryId } = useParams();

  useEffect(() => {
    dispatch(fetchEntryHistory(Number(entryId)));
    dispatch(fetchEntrySummary(Number(entryId)));
  }, [dispatch, entryId]);

  const chipHistory = useSelector((state: RootState) =>
    getEntryChipHistory(state, Number(entryId)),
  );

  const entry = useSelector((state: RootState) =>
    getEntry(state, Number(entryId)),
  );

  const eventsById = useSelector((state: RootState) => getEventsById(state));

  const eventHistory = useSelector((state: RootState) =>
    getEntryEventHistory(state, Number(entryId)),
  );

  const seasonHistory = useSelector((state: RootState) =>
    getEntrySeasonHistory(state, Number(entryId)),
  );

  const movementFromIndex = (index: number): React.ReactNode => {
    const thisRank = eventHistory[index].overall_rank;
    const lastRank = index === 0 ? null : eventHistory[index - 1].overall_rank;
    return <Movement lastRank={lastRank} rank={thisRank} />;
  };

  if (!entry) {
    return null;
  }
  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{t("entryHistory.title", "Entry History")}</Title>
        </Box>

        <Box mb={4}>
          <ScrollTable>
            <Table>
              <thead>
                <tr>
                  <th>
                    <Tooltip content={t("entryHistory.gameday", "Gameday")}>
                      <TooltipLabel>GD</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip
                      content={t(
                        "entryHistory.gamePointsn",
                        "Game Fantasy Points",
                      )}
                    >
                      <TooltipLabel>GP</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip
                      content={t(
                        "entryHistory.pointsOnBench",
                        "Points on Bench",
                      )}
                    >
                      <TooltipLabel>PB</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip content={t("entryHistory.gameRank", "Game Rank")}>
                      <TooltipLabel>GR</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip
                      content={t(
                        "entryHistory.transfersMade",
                        "Transactions Made",
                      )}
                    >
                      <TooltipLabel>TM</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip
                      content={t(
                        "entryHistory.transfersCost",
                        "Transaction Cost",
                      )}
                    >
                      <TooltipLabel>TC</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip
                      content={t(
                        "entryHistory.overallPointsn",
                        "Overall Fantasy Points",
                      )}
                    >
                      <TooltipLabel>OP</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip
                      content={t("entryHistory.overallRank", "Overall Rank")}
                    >
                      <TooltipLabel>OR</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip
                      content={t("entryHistory.teamValue", "Team Value")}
                    >
                      <TooltipLabel>$</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip content={t("entryHistory.movement", "Movement")}>
                      <TooltipLabel>#</TooltipLabel>
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {eventHistory.map((eh, i) => (
                  <tr key={eh.event}>
                    <td>
                      <Link to={`/entry/${entry.id}/event/${eh.event}`}>
                        {getPrefixedEventShortName(eventsById[eh.event].name)}
                      </Link>
                    </td>
                    <td>{integerDivide(eh.points, 10)}</td>
                    <td>{integerDivide(eh.points_on_bench, 10)}</td>
                    <td>{eh.rank ? eh.rank.toLocaleString() : "-"}</td>
                    <td>{eh.event_transfers}</td>
                    <td>{integerToDecimal(eh.event_transfers_cost, 10)}</td>
                    <td>{integerDivide(eh.total_points, 10)}</td>
                    <td>
                      {eh.overall_rank ? eh.overall_rank.toLocaleString() : "-"}
                    </td>
                    <td>{integerToMoney(eh.value, 10)}</td>
                    <td>{movementFromIndex(i)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollTable>
        </Box>
        <Box mb={4}>
          <Box mx={2}>
            <SubHeading>{t("entryHistory.chips", "Chips")}</SubHeading>
          </Box>
          <Table>
            <thead>
              <tr>
                <th scope="col">{t("entryHistory.date", "Date")}</th>
                <th scope="col">{t("entryHistory.name", "Name")}</th>
                <th scope="col">{t("entryHistory.active", "Active")}</th>
              </tr>
            </thead>
            <tbody>
              {chipHistory.map((ch) => (
                <tr key={ch.event}>
                  <td>
                    {formatRawAsLocalI18n(ch.time, dateLocales[i18n.language])}
                  </td>
                  <td>{getChipName(ch.name as ChipName, t)}</td>
                  <td>
                    <Link to={`/entry/${entry.id}/event/${ch.event}`}>
                      {getPrefixedEventShortName(eventsById[ch.event].name)}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
        <Box mb={4}>
          <Box mx={2} mb={2}>
            <SubHeading>
              {t("entryHistory.previousSeasons", "Previous Seasons")}
            </SubHeading>
          </Box>
          <Panel>
            <Table>
              <thead>
                <tr>
                  <th>{t("entryHistory.season", "Season")}</th>
                  <th>{t("entryHistory.points", "Points")}</th>
                  <th>{t("entryHistory.Rank", "Rank")}</th>
                </tr>
              </thead>
              <tbody>
                {seasonHistory.map((sh) => (
                  <tr key={sh.season_name}>
                    <td>{sh.season_name}</td>
                    <td>{sh.total_points}</td>
                    <td>{sh.rank}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Panel>
        </Box>
      </Main>
      <Secondary>
        <Entry entryId={entry.id} />
      </Secondary>
    </Wrapper>
  );
};

export default withTranslation()(EntryHistory);
